import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { PhotoIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { companyService } from '../../api/services';
import { Company } from '../../types';
import { validateMatriculeFiscal, validateRIB } from '../../utils/validators';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import toast from 'react-hot-toast';

export function CompanySettings() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | undefined>(undefined);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Partial<Company>>({
    defaultValues: {
      timbreFiscal: 1.000,
      tauxFodec: 1,
    },
  });

  useEffect(() => {
    loadCompany();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCompany = async () => {
    try {
      setLoading(true);
      const data = await companyService.get();
      // Le backend stocke le taux FODEC en fraction décimale (0.010 = 1 %) ; l'UI le
      // saisit en pourcentage. On convertit pour l'affichage (0.010 → 1).
      reset({ ...data, tauxFodec: data?.tauxFodec != null ? data.tauxFodec * 100 : 1 });
      setLogoFile(null);
      setLogoUrl(data.logoUrl);
      setLogoPreview(data.logoUrl ?? null);
    } catch (error) {
      // Company may not exist yet
    } finally {
      setLoading(false);
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = async (data: Partial<Company>) => {
    try {
      setSaving(true);

      // Carry the already-persisted logo URL so saving the settings doesn't wipe it.
      // A freshly selected file is uploaded first and takes precedence.
      let nextLogoUrl = logoUrl;
      if (logoFile) {
        const logoResult = await companyService.uploadLogo(logoFile);
        nextLogoUrl = logoResult.url;
      }

      // Reconvertit le taux FODEC saisi en % (1) vers la fraction décimale attendue
      // par le backend (0.010), qui calcule FODEC = montant HT × tauxFodec.
      await companyService.update({
        ...data,
        logoUrl: nextLogoUrl,
        tauxFodec: data.tauxFodec != null ? data.tauxFodec / 100 : undefined,
      });

      setLogoUrl(nextLogoUrl);
      setLogoFile(null);
      setLogoPreview(nextLogoUrl ?? null);
      toast.success(t.messages.saveSuccess);
    } catch (error) {
      toast.error(t.messages.saveError);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="page-title">{t.parametres.societe.title}</h1>
        <p className="page-subtitle">
          Configurez les informations de votre société
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Informations générales
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="label">{t.parametres.societe.raisonSociale} *</label>
              <input
                {...register('raisonSociale', { required: t.validation.required })}
                className={`input-field ${errors.raisonSociale ? 'input-error' : ''}`}
              />
              {errors.raisonSociale && (
                <p className="mt-1 text-sm text-red-600">{errors.raisonSociale.message}</p>
              )}
            </div>

            <div>
              <label className="label">{t.parametres.societe.matriculeFiscal} *</label>
              <input
                {...register('matriculeFiscal', {
                  required: t.validation.required,
                  validate: (v) => !v || validateMatriculeFiscal(v) || t.validation.matriculeFiscal,
                })}
                className={`input-field font-mono ${errors.matriculeFiscal ? 'input-error' : ''}`}
                placeholder="1873400/G ou 1234567/A/B/C/000"
              />
              {errors.matriculeFiscal && (
                <p className="mt-1 text-sm text-red-600">{errors.matriculeFiscal.message}</p>
              )}
            </div>

            <div>
              <label className="label">{t.parametres.societe.telephone}</label>
              <input
                {...register('telephone')}
                className="input-field"
                placeholder="+216 71 123 456"
              />
            </div>

            <div>
              <label className="label">{t.parametres.societe.email}</label>
              <input
                type="email"
                {...register('email')}
                className="input-field"
              />
            </div>

            <div>
              <label className="label">{t.parametres.societe.siteWeb}</label>
              <input
                {...register('siteWeb')}
                className="input-field"
                placeholder="https://www.example.tn"
              />
            </div>

            <div className="md:col-span-2">
              <label className="label">{t.parametres.societe.adresse} *</label>
              <textarea
                {...register('adresse', { required: t.validation.required })}
                rows={2}
                className={`input-field ${errors.adresse ? 'input-error' : ''}`}
              />
            </div>

            <div>
              <label className="label">{t.parametres.societe.codePostal}</label>
              <input
                {...register('codePostal')}
                className="input-field"
                placeholder="1000"
              />
            </div>

            <div>
              <label className="label">{t.parametres.societe.ville}</label>
              <input
                {...register('ville')}
                className="input-field"
                placeholder="Tunis"
              />
            </div>
          </div>
        </div>

        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Informations bancaires
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">{t.parametres.societe.rib} *</label>
              <input
                {...register('rib', {
                  required: t.validation.required,
                  validate: (v) => !v || validateRIB(v) || t.validation.rib,
                })}
                className={`input-field font-mono ${errors.rib ? 'input-error' : ''}`}
                placeholder="10 006 035 0000000001 97"
              />
              {errors.rib && (
                <p className="mt-1 text-sm text-red-600">{errors.rib.message}</p>
              )}
            </div>

            <div>
              <label className="label">{t.parametres.societe.banque}</label>
              <input
                {...register('banque')}
                className="input-field"
              />
            </div>
          </div>
        </div>

        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Paramètres fiscaux
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">{t.parametres.societe.timbreFiscal}</label>
              <div className="relative">
                <input
                  type="number"
                  step="0.001"
                  {...register('timbreFiscal', { valueAsNumber: true })}
                  className="input-field pr-16"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                  TND
                </span>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Montant du timbre fiscal applicable sur les paiements en espèces
              </p>
            </div>

            <div>
              <label className="label">{t.parametres.societe.tauxFodec}</label>
              <div className="relative">
                <input
                  type="number"
                  step="0.1"
                  {...register('tauxFodec', { valueAsNumber: true })}
                  className="input-field pr-8"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                  %
                </span>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Taux du Fonds de Développement de la Compétitivité
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Facturation électronique (El Fatoora / TTN)
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  {...register('einvoicingEnabled')}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-900">
                  Activer la facturation électronique pour cette société
                </span>
              </label>
              <p className="mt-1 ml-7 text-xs text-gray-500">
                Une fois activé, les factures pourront être transmises à Tunisie TradeNet (TTN) au format TEIF.
              </p>
            </div>

            <div>
              <label className="label">Identifiant TTN</label>
              <input
                {...register('ttnIdentifiant')}
                className="input-field font-mono"
                placeholder="Identifiant fourni par TTN"
                autoComplete="off"
              />
            </div>

            <div>
              <label className="label">Mot de passe TTN</label>
              <input
                type="password"
                {...register('ttnMotDePasse')}
                className="input-field font-mono"
                placeholder="••••••••"
                autoComplete="new-password"
              />
            </div>

            <div>
              <label className="label">IICE</label>
              <input
                {...register('ttnIice')}
                className="input-field font-mono"
                placeholder="Identifiant Code de l'entreprise"
              />
              <p className="mt-1 text-xs text-gray-500">
                Identifiant Code de l'Entreprise déclaré auprès de TTN.
              </p>
            </div>

            <div className="flex items-end">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  {...register('ttnTestMode')}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-900">
                  Mode test (sandbox TTN)
                </span>
              </label>
            </div>
          </div>
        </div>

        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {t.parametres.societe.logo}
          </h2>
          <div className="flex items-start gap-6">
            <div className="flex-shrink-0">
              {logoPreview ? (
                <img
                  src={logoPreview}
                  alt="Logo"
                  className="h-24 w-24 object-contain border border-gray-200 rounded-lg"
                />
              ) : (
                <div className="h-24 w-24 bg-gray-100 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300">
                  <PhotoIcon className="h-10 w-10 text-gray-400" />
                </div>
              )}
            </div>
            <div>
              <label className="btn-outline cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className="hidden"
                />
                {t.parametres.societe.uploadLogo}
              </label>
              <p className="mt-2 text-xs text-gray-500">
                Formats acceptés : PNG, JPG, SVG. Taille max : 2 Mo.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={loadCompany}
            className="btn-outline"
          >
            {t.common.cancel}
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-primary"
          >
            {saving ? t.common.loading : t.common.save}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CompanySettings;
